/**
 * Event content, kept separate from components so it can later be loaded
 * from Supabase without touching the UI.
 *
 * Rules: only owner-confirmed facts. Unknown values stay `null` and the UI
 * shows "To be confirmed". Keep Turkish spelling and diacritics exact.
 */

export const events = [
  {
    slug: "turkish-republic-day-2026",
    title: "Turkish Republic Day Celebration",
    // ISO date (venue local date). Weekday is derived, never hard-coded.
    date: "2026-10-29",
    startTime: "7:00 PM",
    endTime: null,
    capacity: null,
    availability: null,
    featured: true,
    // Joint event: keys into src/data/brand.js, shown as "A × B".
    partners: ["greyWolf", "lysters"],
    presentedBy: "The Grey Wolf × Lyster's Pub",
    theme: "turkish-republic-day",
    summary:
      "The Grey Wolf and Lyster's Pub invite you to celebrate Turkish Republic Day with a special event menu.",
    description:
      "Republic Day on 29 October marks the proclamation of the Republic of Türkiye in 1923. The Grey Wolf and Lyster's Pub invite you to celebrate the occasion together with a special event menu of starters and a grilled main course.",
    price: {
      amount: 30,
      currency: "EUR",
      basis: "per person",
      note: "Excluding alcoholic drinks",
    },
    menu: {
      starters: [
        "Melon",
        "Feta cheese",
        "Haydari",
        "Spicy ezme",
        "Handmade meat çiğ köfte",
      ],
      main: ["Grilled chicken and grilled köfte"],
    },
    // Poster. `image` = a finished poster uploaded later via the admin
    // dashboard (replaces the composed poster). `portrait` must be an
    // authentic, appropriately licensed historical photograph supplied by the
    // owner. Never generate or fabricate it. Until supplied, a labelled slot shows.
    poster: {
      image: null, // { src, alt }
      headline: "29 EKİM CUMHURİYET BAYRAMI",
      tagline: "Live Music & Turkish Music",
      portrait: {
        src: null,
        alt: "Portrait of Mustafa Kemal Atatürk",
        credit: null, // e.g. photographer / archive
        license: null, // e.g. "Public domain" with source link
      },
    },
    // Entertainment. Performers are NOT confirmed yet: keep `performers`
    // empty until the owner supplies names, photos and descriptions.
    entertainment: {
      highlights: [
        { title: "Live Turkish music", text: "Live Turkish music as part of the celebration." },
        { title: "Turkish music all evening", text: "Turkish music played throughout the evening." },
      ],
      // Performer shape: { id, name, photo: { src, alt } | null, description, time: string | null, sortOrder }
      performers: [],
    },
    // Online ticketing. Disabled until the backend, capacity and SumUp are
    // configured and approved. Capacity and remaining places come ONLY from
    // the server, never from this file.
    ticketing: {
      enabled: false,
    },
    // Display only: no alcohol sales or payment through the website.
    drinks: {
      servedBy: "Lyster's Pub",
      note:
        "Alcoholic drinks are served and charged separately by Lyster's Pub. Drinks are not included in the €30 food menu.",
      items: [
        {
          name: "YENİ RAKI",
          options: [
            { label: "Single", price: 6 },
            { label: "Double", price: 11 },
            { label: "70cl Bottle", price: 100 },
          ],
        },
      ],
    },
  },
];

export const getEventBySlug = (slug) => events.find((e) => e.slug === slug);

export const featuredEvent = events.find((e) => e.featured);

/** "Thursday, 29 October 2026" — formatted in UTC so the date never shifts. */
export const formatEventDate = (iso, options) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-IE", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    ...options,
  });

/** { day: "29", month: "Oct", year: "2026" } for date badges. */
export const eventDateParts = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return {
    day: String(d.getUTCDate()),
    month: d.toLocaleDateString("en-IE", { timeZone: "UTC", month: "short" }),
    year: String(d.getUTCFullYear()),
  };
};

export const formatPrice = ({ amount, currency }) =>
  new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
